import CheckCircle from "@mui/icons-material/CheckCircle";
import Circle from "@mui/icons-material/Circle";
import Error from "@mui/icons-material/Error";
import Info from "@mui/icons-material/Info";
import Star from "@mui/icons-material/Star";
import {
  ActivityType,
  FacilityPilotProgramReadyStatus,
  FacilityPilotProgramStatus,
  LocalJurisdictionType,
  TierIiReportEncampStatus,
  TierIiReportOrgStatus,
} from "generated-graphql/graphql";

// Shared type definitions
export type PilotFacilityRow = {
  facilityId: string;
  facilityName: string;
  streetAddress1?: string | null;
  city?: string | null;
  facilityState: string;
  tenantId: string;
  tenantName: string;
  isDeniedTenant: boolean;
  reportId?: string | null;
  reportOrgStatus?: TierIiReportOrgStatus | null;
  reportEncampStatus?: TierIiReportEncampStatus | null;
  workflowStatus?: string | null;
  workflowId?: string | null;
  workflowTags: Array<{ id: string; name: string }>;
  configId: string;
  configNotes?: string | null;
  configCreatedAt: Date;
  configCreatedBy: string;
  currentActivityType?: ActivityType | null;
  currentActivityDisplayName?: string | null;
  isCohort: boolean;
};

export type StateRow = {
  state: string;
  stateName: string;
  verifiedReportCount: number;
  pilotFacilityCount: number;
  totalReportingFacilities: number;
  status: FacilityPilotProgramStatus;
  isStateEnabled: boolean;
  portalTech: string;
  readyStatus: FacilityPilotProgramReadyStatus;
  notes?: string | null;
  documentationComplete: boolean;
  qaComplete: boolean;
  pilotFacilities: PilotFacilityRow[];
};

export type LocalJurisdictionRow = {
  state: string;
  localJurisdictionName: string;
  reportingYear: number;
  verifiedReportCount: number;
  pilotFacilityCount: number;
  totalReportingFacilities: number;
  status: FacilityPilotProgramStatus;
  readyStatus: FacilityPilotProgramReadyStatus;
  notes?: string | null;
  documentationComplete: boolean;
  qaComplete: boolean;
  jurisdictionType?: LocalJurisdictionType | null;
  jurisdictionMatchKey?: string | null;
  pilotFacilities: PilotFacilityRow[];
};

export type ViewType = "states" | "local";

export type StatusFilter = FacilityPilotProgramStatus | "enabled" | "notEnabled" | "started" | null;

// Utility functions
export const getStatusLabel = (status: FacilityPilotProgramStatus): string => {
  switch (status) {
    case FacilityPilotProgramStatus.NotStarted:
      return "Not Started";
    case FacilityPilotProgramStatus.InError:
      return "Error";
    case FacilityPilotProgramStatus.InProgress:
      return "In Progress";
    case FacilityPilotProgramStatus.Completed:
      return "Completed";
    default:
      return status;
  }
};

// Compute unique tags from all workflows in a state or local jurisdiction
export const getStateTags = (row: StateRow | LocalJurisdictionRow): string[] => {
  const allTags = new Set<string>();
  row.pilotFacilities.forEach((facility) => {
    facility.workflowTags?.forEach((tag) => {
      allTags.add(tag.name);
    });
  });
  return Array.from(allTags).sort();
};

// Get status icon component
export const getStatusIcon = (status: FacilityPilotProgramStatus, isStateEnabled: boolean) => {
  // Allowed state takes precedence over all other statuses
  if (isStateEnabled) {
    return <Star fontSize="small" sx={{ color: "#FFD700", opacity: 0.9 }} />;
  }

  switch (status) {
    case FacilityPilotProgramStatus.Completed:
      return <CheckCircle fontSize="small" sx={{ color: "success.light", opacity: 0.8 }} />;
    case FacilityPilotProgramStatus.InError:
      return <Error fontSize="small" sx={{ color: "error.light", opacity: 0.8 }} />;
    case FacilityPilotProgramStatus.InProgress:
      return <Info fontSize="small" sx={{ color: "info.light", opacity: 0.8 }} />;
    case FacilityPilotProgramStatus.NotStarted:
    default:
      return <Circle fontSize="small" sx={{ color: "text.disabled", opacity: 0.6 }} />;
  }
};

// Get status icon component without star (for checklist column)
export const getStatusIconWithoutStar = (status: FacilityPilotProgramStatus) => {
  switch (status) {
    case FacilityPilotProgramStatus.Completed:
      return <CheckCircle fontSize="small" sx={{ color: "success.light", opacity: 0.8 }} />;
    case FacilityPilotProgramStatus.InError:
      return <Error fontSize="small" sx={{ color: "error.light", opacity: 0.8 }} />;
    case FacilityPilotProgramStatus.InProgress:
      return <Info fontSize="small" sx={{ color: "info.light", opacity: 0.8 }} />;
    case FacilityPilotProgramStatus.NotStarted:
    default:
      return <Circle fontSize="small" sx={{ color: "text.disabled", opacity: 0.6 }} />;
  }
};
